// src/screens/ClubArticlePublisher.tsx
import { useState, FormEvent, useRef, ChangeEvent } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Newspaper,
  Image as ImageIcon,
  Bold,
  Italic,
  Plus,
  Minus,
  Palette,
} from "lucide-react";

const INLINE_IMAGES_BUCKET = "club-article-images"; // bucket Supabase

export function ClubArticlePublisher() {
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [publishNow, setPublishNow] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null); // image WP
  const [categoryId, setCategoryId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<"success" | "error" | "info">(
    "info"
  );

  const editorRef = useRef<HTMLDivElement | null>(null);
  const inlineImageInputRef = useRef<HTMLInputElement | null>(null);
  const colorPickerRef = useRef<HTMLInputElement | null>(null);

  /** Force le focus dans l’éditeur */
  const focusEditor = () => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
  };

  /** Applique une commande (gras, italic, color, etc.) */
  const execCommand = (cmd: string, value?: string) => {
    focusEditor();
    document.execCommand(cmd, false, value);
  };

  const handleBold = () => execCommand("bold");
  const handleItalic = () => execCommand("italic");

  // --- Taille de police ---
  const increaseFont = () => execCommand("fontSize", "5"); // 5 = gros
  const decreaseFont = () => execCommand("fontSize", "2"); // 2 = petit

  // --- Couleur ---
  const handleColorPick = () => {
    colorPickerRef.current?.click();
  };

  const applyColor = (color: string) => {
    execCommand("foreColor", color);
  };

  // Upload de l'image inline dans Supabase Storage
  const uploadInlineImage = async (file: File): Promise<string> => {
    const safeName = file.name.replace(/[^a-z0-9_-]/gi, "-").toLowerCase();
    const path = `wp-inline/${Date.now()}-${safeName}`;

    const { error } = await supabase.storage
      .from(INLINE_IMAGES_BUCKET)
      .upload(path, file);

    if (error) throw error;

    const { data } = supabase.storage
      .from(INLINE_IMAGES_BUCKET)
      .getPublicUrl(path);

    return data.publicUrl;
  };

  // Quand l’utilisateur choisit une image pour l’insérer
  const handleInlineImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const url = await uploadInlineImage(file);
      execCommand("insertImage", url);
    } catch (err: any) {
      setMessageType("error");
      setMessage("❌ Erreur upload image : " + err.message);
    } finally {
      e.target.value = "";
    }
  };

  const fileToBase64 = async (file: File): Promise<string> => {
    const buf = await file.arrayBuffer();
    return btoa(String.fromCharCode(...new Uint8Array(buf)));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const editorHTML = editorRef.current?.innerHTML || "";

      let imageBase64 = null;
      let imageName = null;
      let imageType = null;

      if (imageFile) {
        imageBase64 = await fileToBase64(imageFile);
        imageName = imageFile.name;
        imageType = imageFile.type;
      }

      const categoryIdNum =
        categoryId.trim() === "" ? null : Number(categoryId.trim());

      const { error } = await supabase.functions.invoke(
        "publish_wp_article",
        {
          body: {
            title,
            content: editorHTML,
            status: publishNow ? "publish" : "draft",
            imageBase64,
            imageName,
            imageType,
            categoryId: categoryIdNum,
          },
        }
      );

      if (error) throw error;

      setMessageType("success");
      setMessage("✅ Article envoyé !");
      editorRef.current!.innerHTML = "";
      setTitle("");
      setCategoryId("");
      setPublishNow(false);
      setImageFile(null);
    } catch (err: any) {
      setMessageType("error");
      setMessage("❌ " + err.message);
    }

    setLoading(false);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-slate-600"
      >
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>

      <div className="flex items-center gap-3">
        <div className="p-2 bg-indigo-100 rounded-xl text-indigo-700">
          <Newspaper className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Publier un article</h1>
          <p className="text-sm text-slate-500">Éditeur simple et intuitif.</p>
        </div>
      </div>

      {message && (
        <div
          className={`px-3 py-2 rounded-lg text-sm border ${
            messageType === "success"
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-red-50 text-red-700 border-red-200"
          }`}
        >
          {message}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="bg-white p-4 rounded-xl border space-y-4"
      >
        {/* Titre */}
        <div>
          <label className="text-sm font-medium">Titre</label>
          <input
            className="w-full border rounded-lg px-3 py-2 mt-1"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>

        {/* Barre d’outils */}
        <div className="flex flex-wrap items-center gap-2 p-2 bg-slate-50 border rounded-lg">
          <span className="text-xs text-slate-500 mr-2">Style :</span>

          <button type="button" onClick={handleBold} className="btn-mini">
            <Bold className="w-3 h-3" /> Gras
          </button>

          <button type="button" onClick={handleItalic} className="btn-mini">
            <Italic className="w-3 h-3" /> Italique
          </button>

          {/* Couleur */}
          <button
            type="button"
            onClick={handleColorPick}
            className="btn-mini flex items-center gap-1"
          >
            <Palette className="w-3 h-3" /> Couleur
          </button>

          <input
            ref={colorPickerRef}
            type="color"
            className="hidden"
            onChange={(e) => applyColor(e.target.value)}
          />

          {/* couleurs rapides */}
          <button type="button" onClick={() => applyColor("#000")} className="btn-mini">Noir</button>
          <button type="button" onClick={() => applyColor("#e11d48")} className="btn-mini">Rouge</button>
          <button type="button" onClick={() => applyColor("#2563eb")} className="btn-mini">Bleu</button>

          {/* Taille */}
          <button type="button" onClick={increaseFont} className="btn-mini">
            <Plus className="w-3 h-3" /> + Taille
          </button>

          <button type="button" onClick={decreaseFont} className="btn-mini">
            <Minus className="w-3 h-3" /> - Taille
          </button>

          {/* Image */}
          <button
            type="button"
            onClick={() => inlineImageInputRef.current?.click()}
            className="btn-mini"
          >
            <ImageIcon className="w-3 h-3" /> Image
          </button>

          <input
            ref={inlineImageInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleInlineImageUpload}
          />
        </div>

        {/* Éditeur WYSIWYG */}
        <div
          ref={editorRef}
          contentEditable
          className="min-h-[220px] border rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        ></div>

        {/* Image de couverture WP */}
        <div>
          <label className="text-sm font-medium">Image de couverture WP</label>
          <input
            type="file"
            accept="image/*"
            className="mt-1"
            onChange={(e) =>
              setImageFile(e.target.files ? e.target.files[0] : null)
            }
          />
        </div>
{/* Catégorie WordPress */}
<div>
  <label className="text-sm font-medium">ID de la catégorie WordPress</label>
  <input
    type="number"
    className="w-full border rounded-lg px-3 py-2 mt-1"
    value={categoryId}
    onChange={(e) => setCategoryId(e.target.value)}
    placeholder="Ex : 4"
  />
  <p className="text-xs text-slate-500 mt-1">
    Entrez l’ID de la catégorie WordPress (ex. : 24 pour “Actualités”).
  </p>
</div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={publishNow}
            onChange={(e) => setPublishNow(e.target.checked)}
          />
          <span className="text-sm">Publier directement</span>
        </div>

        <button
          type="submit"
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg"
          disabled={loading}
        >
          {loading ? "Envoi..." : "Envoyer l’article"}
        </button>
      </form>

      <style>{`
        .btn-mini {
          border: 1px solid #CBD5E1;
          padding: 3px 6px;
          border-radius: 6px;
          background: white;
          font-size: 11px;
        }
        .btn-mini:hover {
          background:#f1f5f9;
        }
      `}</style>
    </div>
  );
}
